import { useState, useEffect, useRef, useMemo } from "react";
import classnames from "classnames";
import { useResponsive } from "@outplayed/responsive";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { useTable, useSortBy, SortingRule, Column } from "react-table";
import { Against } from "@ugg/shared/components/TierList/common/Against";
import { ChampionLink } from "@ugg/shared/components/TierList/common/ChampionLink";
import { TooltipContainer } from "@outplayed/tooltips";
import { tierListSettings } from "@ugg/shared/components/TierList/lib/tier-list-settings";
import { SPECIAL_TIER_LIST_PAGES, useTierListPage } from "@ugg/shared/pages/tier-list-pages";
import {
  TableScrollHandler,
  useLazyLoadTableRows,
  TIER_LIST_PAGE_SIZE,
} from "@ugg/shared/components/TierList/common/TableScrollHandler";
import { TierListChampionInfo } from "@ugg/shared/api/data-parser/tier-lists/tier-list";
import { getRoleImg } from "@ugg/shared/utils/role-helpers";
import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import { getTierColor, getLuTierColor, getLuTier, getTierListPickRateCutoff } from "@ugg/shared/utils/tier-helpers";
import { numberWithCommas } from "@ugg/shared/utils/math";
import { isChampionTransform } from "@ugg/shared/utils/multi-build-helpers";
import { ReactComponent as QuestionCircle } from "@ugg/shared/assets/svg/question-circle.svg";
// import { SpriteImageScaler } from "@ugg/shared/components/common/SpriteImageScaler";

interface TierListTableProps {
  data: TierListChampionInfo[];
  filters: {
    queueType?: Partial<QueueTypeS>;
    allChampions?: "true" | "false";
  };
}

export const TierListTable = (props: TierListTableProps) => {
  const { getChampionName } = getRiotAssetsContext();
  const prevSort = useRef<SortingRule<TierListChampionInfo>[] | null>(null);
  const { currentBreakpoint } = useResponsive();
  const [pageSize, setPageSize] = useState(TIER_LIST_PAGE_SIZE);
  const tierListPage = useTierListPage();

  const { filters, data } = props;
  const isOneForAll = filters.queueType === QueueTypeS.ONE_FOR_ALL;
  const { showAgainst, showBan, showPick, showRole, showTier } = (filters.queueType && tierListSettings[filters.queueType]) || {};
  const isSpecialGameMode = Object.values(SPECIAL_TIER_LIST_PAGES).find((page) => page === tierListPage);

  const tableData = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.filter((champion) => {
      if (isOneForAll) {
        if (champion.champion_id === "30" || champion.champion_id === "17") {
          // ignore Karthus (30) and Teemo (17) who are removed from One for All
          return false;
        }
        return true;
      } else if (isChampionTransform(parseInt(champion.champion_id))) {
        // No red blue kayn (transform ids like 100141/200141) — but keep
        // League Classic champions (60000–69999), which are real champions.
        return false;
      } else if (isSpecialGameMode || filters.allChampions === "true") {
        return true;
      }
      return champion.tier.pick_rate >= getTierListPickRateCutoff(filters.queueType);
    });
  }, [data, filters.queueType, filters.allChampions]);

  // Tablet and smaller
  const isMobile = ["MOBILE_SMALL", "MOBILE_MEDIUM", "MOBILE_LARGE"].includes(currentBreakpoint);
  // Phone size
  const isMobileSmall = ["MOBILE_SMALL", "MOBILE_MEDIUM"].includes(currentBreakpoint);

  const hiddenColumns = [
    !showAgainst && "worst_against",
    !showBan && "ban_rate",
    !showPick && "pick_rate",
    !showRole && "role",
    !showTier && "tier",
  ]
    .filter((col) => !!col)
    .map((col) => String(col));

  // Later test better cell/sort typing
  const columns = useMemo<Column<TierListChampionInfo>[]>(
    () => [
      {
        id: "rank",
        accessor: "role",
        Header: <span>Rank</span>,
        responsiveClassName: "xxxs:w-[40px] xs:w-[60px] xxxs:min-w-[40px] xs:min-w-[40px] xxxs:max-w-[40px] xs:max-w-[60px]",
        headerClassName: "rank xxxs:max-xs:[&>*]:hidden",
        className: "rank",
        Cell: ({ index }) => <span>{index + 1}</span>,
        sortable: false,
      },
      {
        Header: "Role",
        responsiveClassName: "xxxs:max-xs:!hidden xxxs:min-w-[30px] xs:min-w-[40px] xxxs:max-w-[30px] xs:max-w-[60px]",
        headerClassName: "role",
        className: "role",
        accessor: "role",
        Cell: ({ value }) => (
          <div>
            <img className="w-[20px] h-[20px]" src={getRoleImg(value)} alt={value} />
          </div>
        ),
      },
      {
        Header: <div className="flex justify-center flex-1">Champion</div>,
        // xxxs:min-w-[40px] xs:min-w-[155px] xxxs:max-w-[100px] xs:max-w-[180px]
        responsiveClassName: `
          xxxs:min-w-[40px] xs:min-w-[155px] xs:max-w-[180px]
          max-[800px]:!min-w-[70px] 
          max-[800px]:!flex-[70] max-[800px]:!justify-center
          min-[800px]:!justify-start max-[600px]:!min-w-[40px] 
          max-[600px]:!flex-[40]
        `,
        headerClassName: "champion max-xs:[&>*]:hidden",
        className: "champion xs:!pl-[12px] xs:!pr-[0px]",
        accessor: "champion_link",
        sortInverted: true,
        sortType: (a, b) => {
          const champNameA = getChampionName(a.values.champion_link.champion_id);
          const champNameB = getChampionName(b.values.champion_link.champion_id);
          return champNameB.localeCompare(champNameA);
        },
        Cell: ({ value }) => (
          <ChampionLink
            className="max-[800px]:[&_.champion-name]:hidden max-[800px]:!pl-0 xs:[&_.role]:hidden"
            championId={Number(value.champion_id)}
            role={value.role}
            dim={isMobileSmall ? 24 : 30}
            showRoleIcon
            // ImgWrapper={({children}: any) =>
            //   <SpriteImageScaler
            //     type="className"
            //     className="flex items-center justify-center"
            //     dimClassName="xxxs:w-[24px] xxxs:h-[24px] xxxs:w-[30px] xxxs:h-[30px]"
            //     scaleClassName="xxxs:scale-[0.5] xxxs:scale-[0.5] xs:scale-[0.625] xs:scale-[0.625]"
            //   >
            //     {children}
            //   </SpriteImageScaler>
            // }
          />
        ),
      },
      {
        Header: () => (
          <TooltipContainer
            tooltipInfo={
              <span>
                The U.GG tier list is derived from standard deviations on
                <br />
                champion winrates, pick rates and ban rates to identify the
                <br />
                strongest champions in the current meta.
              </span>
            }
          >
            <div className="flex">
              Tier
              <QuestionCircle className="ml-[5px] h-[15px] w-[17px] fill-lavender-50 max-sm:hidden" />
            </div>
          </TooltipContainer>
        ),
        id: "tier",
        accessor: "tier",
        responsiveClassName: "xxxs:min-w-[40px] xs:min-w-[50px] xxxs:max-w-[50px] xs:max-w-[90px]",
        headerClassName: "tier",
        className: "tier",
        sortDescFirst: true,
        sortType: (a, b) => {
          const stdevsA = a.values.tier.stdevs;
          const stdevsB = b.values.tier.stdevs;
          const pickA = a.values.tier.pick_rate;
          const pickB = b.values.tier.pick_rate;
          const pickrate = getTierListPickRateCutoff();

          if (stdevsA > stdevsB) {
            if (pickA > pickrate && pickB > pickrate) return 1;
            else if (pickA > pickrate && pickB <= pickrate) return 1;
            else if (pickA <= pickrate && pickB > pickrate) return -1;
            else if (pickA <= pickrate && pickB <= pickrate) {
              if (stdevsA > stdevsB) return 1;
              else if (stdevsA < stdevsB) return -1;
            }
          } else if (stdevsA < stdevsB) {
            if (pickA > pickrate && pickB > pickrate) return -1;
            else if (pickA > pickrate && pickB <= pickrate) return 1;
            else if (pickA <= pickrate && pickB > pickrate) return -1;
            else if (pickA <= pickrate && pickB <= pickrate) {
              if (stdevsA > stdevsB) return 1;
              else if (stdevsA < stdevsB) return -1;
            }
          }
          return 0;
        },
        Cell: ({ value }) =>
          isSpecialGameMode || value.pick_rate >= getTierListPickRateCutoff(filters.queueType) ? (
            <span className={getLuTierColor(Number(value.stdevs.toFixed(2)))}>
              <b>{getLuTier(Number(value.stdevs.toFixed(2)))}</b>
            </span>
          ) : (
            <span>
              <b>?</b>
            </span>
          ),
      },
      {
        id: "winrate",
        Header: "Win Rate",
        accessor: "win_rate",
        responsiveClassName: "xxxs:min-w-[60px] max-xs:max-w-[80px] xs:min-w-[70px] max-w-[90px]",
        headerClassName: "winrate",
        className: "winrate",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.values.winrate - b.values.winrate;
        },
        Cell: ({ value }) => (
          <span className={getTierColor(Number(value.toFixed(2)))}>
            <b>{value.toFixed(2) + "%"}</b>
          </span>
        ),
      },
      {
        Header: "Pick Rate",
        accessor: "pick_rate",
        responsiveClassName: "xxxs:max-sm:!hidden xxxs:min-w-[60px] xs:min-w-[80px] xxxs:max-w-[60px] xs:max-w-[90px]",
        headerClassName: "pickrate",
        className: "pickrate",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.values.pick_rate - b.values.pick_rate;
        },
        Cell: ({ value }) => <span>{value.toFixed(1) + "%"}</span>,
      },
      {
        Header: "Ban Rate",
        accessor: "ban_rate",
        responsiveClassName: "xxxs:max-xs:!hidden xxxs:min-w-[60px] xs:min-w-[70px] xxxs:max-w-[60px] xs:max-w-[90px]",
        headerClassName: "banrate",
        className: "banrate",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.values.ban_rate - b.values.ban_rate;
        },
        Cell: ({ value }) => <span>{value.toFixed(1) + "%"}</span>,
      },
      {
        Header: () => (
          <span>
            <span className="xxxs:max-xs:hidden">Counter Picks</span>
            <span className="xs:hidden">Counters</span>
          </span>
        ),
        accessor: "worst_against",
        responsiveClassName: `
          xs:max-sm:min-w-[130px] xxxs:min-w-[90px] sm:min-w-[250px] 
          xs:max-sm:max-w-[160px] xxxs:max-w-[90px] sm:max-w-[270px]
        `,
        headerClassName: "against",
        className: "against",
        disableSortBy: true,
        Cell: ({ value }) => (
          <Against
            dim={isMobileSmall ? 18 : 24}
            data={value.bad_against}
            role={value.role}
            championId={Number(value.champion_id)}
            showCount={isMobile ? 3 : 7}
            showSeeMore
          />
        ),
      },
      {
        Header: "Matches",
        accessor: "matches",
        responsiveClassName: "xxxs:min-w-[60px] max-xs:max-w-[70px] xs:min-w-[80px] max-w-[90px]",
        headerClassName: "matches",
        className: "matches",
        sortDescFirst: true,
        Cell: ({ value }) => <span>{numberWithCommas(value)}</span>,
      },
    ],
    [currentBreakpoint, filters],
  );

  const getMaxWidth = () => {
    let maxWidth = undefined;
    if (isSpecialGameMode) {
      maxWidth = "unset";
    }
    return maxWidth;
  };

  const columnStyle = () => ({
    maxWidth: getMaxWidth(),
    flex: `1 1 100%`,
  });

  const { state, getTableProps, getTableBodyProps, headerGroups, rows, preSortedRows, prepareRow, setHiddenColumns } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        sortBy: prevSort.current || [{ id: "winrate", desc: true }],
        hiddenColumns,
      },
      disableSortRemove: true,
    },
    useSortBy,
  );

  useEffect(() => {
    setHiddenColumns(hiddenColumns);
  }, [filters]);

  useEffect(() => {
    setPageSize(TIER_LIST_PAGE_SIZE);
  }, [tableData]);

  const tableRef = useRef<HTMLDivElement>(null);
  useLazyLoadTableRows({
    tableData,
    tableRef,
    pageSize,
    setPageSize,
  });

  useEffect(() => {
    if (prevSort.current) {
      if (prevSort.current[0].id !== state.sortBy[0].id || prevSort.current[0].desc !== state.sortBy[0].desc) {
        prevSort.current = state.sortBy;
        setPageSize(TIER_LIST_PAGE_SIZE);
      }
    } else {
      prevSort.current = state.sortBy;
    }
  }, [state.sortBy]);

  return (
    <div>
      <div className="w-full overflow-x-auto rounded-sm bg-purple-400">
        <div
          ref={tableRef}
          {...getTableProps({
            className: "content-section ReactTable ugg-table-2 tier-list min-w-[400px]",
          })}
        >
          <div className={`rt-thead -header`}>
            {headerGroups.map((headerGroup, index) => (
              <div {...headerGroup.getHeaderGroupProps({ className: "rt-tr" })}>
                {headerGroup.headers.map((column, index) => {
                  const thClassNames = classnames([
                    "rt-th",
                    column.headerClassName,
                    column.responsiveClassName,
                    column.isSorted && !column.isSortedDesc ? "-sort-asc" : "",
                    column.isSorted && column.isSortedDesc ? "-sort-desc" : "",
                  ]);

                  return (
                    <div
                      className={thClassNames}
                      {...column.getHeaderProps(
                        column.getSortByToggleProps({
                          style: columnStyle(),
                        }),
                      )}
                    >
                      {column.render("Header")}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div {...getTableBodyProps({ className: "rt-tbody" })}>
            {rows.map((row, i) => {
              if (i >= pageSize) {
                return null;
              }

              prepareRow(row);

              return (
                <div key={i} className="rt-tr-group xs:max-sm:flex">
                  <div {...row.getRowProps({ className: "rt-tr" })}>
                    {row.cells.map((cell, index) => {
                      const tdClassNames = classnames("rt-td", cell.column.responsiveClassName, cell.column.className, {
                        sorted: cell.column.isSorted,
                        "is-in-odd-row": i % 2 === 0,
                      });

                      return (
                        <div
                          {...cell.getCellProps({
                            className: tdClassNames,
                            style: columnStyle(),
                          })}
                        >
                          {cell.render("Cell", { index: i })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <TableScrollHandler />
    </div>
  );
};
